import { useState, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { useAuth } from '../contexts/AuthContext';
import { spaService } from '../services/spaService';

const HorseRegistration = () => {
  const toast = useRef(null);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    horseName: '',
    dateOfBirth: null,
    gender: null,
    height: '',
    colour: '',
    breed: '',
    breedSocietyRegistered: false,
    breedSocietyDetails: ''
  });


  const genderOptions = [
    { label: 'Mare', value: 'mare' },
    { label: 'Stallion', value: 'stallion' },
    { label: 'Gelding', value: 'gelding' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateOrder = async (e) => {    
    e.preventDefault();
    
      // Basic validation
      if (!formData.horseName || !formData.dateOfBirth || !formData.gender) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please fill in all required fields.',
          life: 3000
        });
        return;
      }
      // Create the order first
      const order = await spaService.createOrder({
        currencyCode: 'GBP',
        orderNotes: `Horse Registration - ${formData.horseName || 'New Horse'}`,
        customer: {
          firstName: user?.member?.firstName || '',
          lastName: user?.member?.lastName || '',
          email: user?.member?.email || '',
          phone: user?.member?.phone || '',
          billingAddress: {            
            addressLine1: user?.member?.addressDetails?.addressLine1 || '',
            addressLine2: user?.member?.addressDetails?.addressLine2 || '',
            city: user?.member?.addressDetails?.townCity || '',
            state: user?.member?.addressDetails?.county || '',
            postalCode: user?.member?.addressDetails?.postCode || '',
            countryCode: user?.member?.addressDetails?.country || 'GB'
          }
        }
      });

      if (!order || !order.orderId) {
        throw new Error('Failed to create order');
      }

      // SE: 26450abd-1dea-43fd-bda5-ff1728b1c5db
      // DEV: f4eaf551-b849-4487-b3e4-e7e3d9c4f464
      await spaService.addOrderLine(order.orderId, "26450abd-1dea-43fd-bda5-ff1728b1c5db", 1, {
        "order-line-is-horse": "True",
        "order-line-horse-name": formData?.horseName || '',
        "order-line-horse-dob": formData?.dateOfBirth || '',
        "order-line-horse-gender": formData?.gender || '',
        "order-line-horse-breed": formData?.breed || '',
        "order-line-horse-colour": formData?.colour || '',
        "order-line-horse-height": formData?.height || '',
        "order-line-horse-socreg": formData?.breedSocietyRegistered ? 'True' : 'False',
        "order-line-horse-socdetails": formData?.breedSocietyDetails || '',
        "order-line-memberid": user?.memberId || ''
      });

      window.location.href = `https://www.sportendurance.co.uk/checkout/checkout-details`;
          
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="se-card mb-4">
        <div className="se-card-header">
          <h2>Register a Horse</h2>
        </div>
        <div className="se-card-content">
          <div className="grid">
            <div className="col-12 md:col-8">
              <p className="text-600 mb-3">
                Register your horse to participate in Sport Endurance events. Registration fee is £5 per horse.
              </p>
              <p className="text-600 m-0">
                Once you have registered a horse on your account, you can select them to ride on your events.
              </p>
            </div>
            <div className="col-12 md:col-4">
              <Card className="text-center se-bg-tertiary text-white">
                <h3 className="mt-0 mb-2">Registration Fee</h3>
                <div className="text-3xl font-bold">£5.00</div>
                <p className="mb-0 mt-1 opacity-90">per horse</p>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <div className="se-card">
        <div className="se-card-header">
          <h3>Horse Details</h3>
        </div>
        <div className="se-card-content">
          <form onSubmit={handleCreateOrder}>
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="se-form-group">
                  <label htmlFor="horseName" className="se-form-label">
                    Horse Name <span className="se-form-required">*</span>
                  </label>
                  <InputText
                    id="horseName"
                    value={formData.horseName}
                    onChange={(e) => handleInputChange('horseName', e.target.value)}
                    className="w-full"
                    required
                  />
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="se-form-group">
                  <label htmlFor="dateOfBirth" className="se-form-label">
                    Date of Birth <span className="se-form-required">*</span>
                  </label>
                  <Calendar
                    id="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.value)}
                    dateFormat="dd/mm/yy"
                    className="w-full"
                    maxDate={new Date()}
                    yearRange="1990:2024"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid">
              <div className="col-12 md:col-4">
                <div className="se-form-group">
                  <label htmlFor="gender" className="se-form-label">
                    Gender <span className="se-form-required">*</span>
                  </label>
                  <Dropdown
                    id="gender"
                    value={formData.gender}
                    options={genderOptions}
                    onChange={(e) => handleInputChange('gender', e.value)}
                    placeholder="Select gender"
                    className="w-full"
                    required
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="se-form-group">
                  <label htmlFor="height" className="se-form-label">
                    Height <span className="se-form-required">*</span>
                  </label>
                  <InputText
                    id="height"
                    value={formData.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    placeholder="e.g. 15.2hh"
                    className="w-full"
                    required
                  />
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="se-form-group">
                  <label htmlFor="colour" className="se-form-label">
                    Colour <span className="se-form-required">*</span>
                  </label>
                  <InputText
                    id="colour"
                    value={formData.colour}
                    onChange={(e) => handleInputChange('colour', e.target.value)}
                    placeholder="e.g. Bay, Chestnut"
                    className="w-full"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="se-form-group">
              <label htmlFor="breed" className="se-form-label">
                Breed <span className="se-form-required">*</span>
              </label>
              <InputText
                id="breed"
                value={formData.breed}
                onChange={(e) => handleInputChange('breed', e.target.value)}
                placeholder="e.g. Arabian, Thoroughbred"
                className="w-full"
                required
              />
            </div>

            <div className="se-form-group">
              <div className="flex align-items-center">
                <Checkbox
                  inputId="breedSocietyRegistered"
                  checked={formData.breedSocietyRegistered}
                  onChange={(e) => handleInputChange('breedSocietyRegistered', e.checked)}
                />
                <label htmlFor="breedSocietyRegistered" className="ml-2 cursor-pointer">
                  Breed Society Registered
                </label>
              </div>
            </div>

            {formData.breedSocietyRegistered && (
              <div className="se-form-group">
                <label htmlFor="breedSocietyDetails" className="se-form-label">
                  Breed Society Registration Details
                </label>
                <InputText
                  id="breedSocietyDetails"
                  value={formData.breedSocietyDetails}
                  onChange={(e) => handleInputChange('breedSocietyDetails', e.target.value)}
                  placeholder="Registration number and society name"
                  className="w-full"
                />
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button
                type="submit"
                label="Register Horse (£5.00)"
                className="p-button-primary"
                icon="pi pi-check"
              />
              <Button
                type="button"
                label="Cancel"
                className="p-button-secondary"
                icon="pi pi-times"
              />
            </div>
          </form>

          <div className="mt-4">
            <Message
              severity="info"
              text="Registration fee is £5.00 per horse. Payment will be processed securely during checkout."
              className="w-full"
            />
          </div>
        </div>
      </div>

    </div>
  );
};

export default HorseRegistration;